/**
 * FAIR Compass Cost Module - Cost Model
 * Ported from Pistoia Alliance FAIR-ValuePath-Calculator
 *
 * Default data structures, initialization functions, and recalculation logic
 */

import {
  PersonnelCosts,
  TechnologyCosts,
  TrainingCosts,
  MigrationCosts,
  ComplianceCosts,
  VendorCosts,
  RiskCosts,
  DisruptionCosts,
  FinancialParameters,
  DurationConfig,
  ProjectCosts,
  CostSummary,
} from './costTypes';

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Generate a unique ID
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Format currency value for display
 */
export function formatCurrency(value: number, currency: string = 'USD'): string {
  const symbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£'
  };

  const symbol = symbols[currency] || '$';

  if (value >= 1000000) {
    return `${symbol}${(value / 1000000).toFixed(2)}M`;
  } else if (value >= 1000) {
    return `${symbol}${(value / 1000).toFixed(0)}K`;
  } else {
    return `${symbol}${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
}

/**
 * Format percentage value
 */
export function formatPercentage(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

// ============================================================================
// Default Data Creators
// ============================================================================

/**
 * Create default personnel costs structure
 */
export function createDefaultPersonnelCosts(): PersonnelCosts {
  return {
    roles: [
      {
        id: generateId(),
        role: 'Data Engineer',
        fte: 1.0,
        duration_months: 12,
        loaded_rate: 90,
        opportunity_cost: 0,
        notes: '2 Data Engineers @ $180K/year, 50% allocation each'
      },
      {
        id: generateId(),
        role: 'Data Architect',
        fte: 0.75,
        duration_months: 12,
        loaded_rate: 100,
        opportunity_cost: 0,
        notes: '1 Data Architect @ $200K/year, 75% allocation'
      },
      {
        id: generateId(),
        role: 'Knowledge Engineer',
        fte: 0.5,
        duration_months: 12,
        loaded_rate: 100,
        opportunity_cost: 0,
        notes: '1 Knowledge Engineer @ $200K/year, 50% allocation'
      }
    ],
    total: 0,
    blended_rate: 0,
    included: true
  };
}

/**
 * Create default technology costs structure
 */
export function createDefaultTechnologyCosts(): TechnologyCosts {
  return {
    capex: [
      {
        id: generateId(),
        item: 'FAIR Evaluation Product',
        cost: 150000,
        depreciation_years: 3,
        notes: 'FAIR assessment and evaluation platform'
      }
    ],
    opex: [
      {
        id: generateId(),
        item: 'Platform Licensing & Maintenance',
        monthly_cost: 3000,
        duration_months: 36,
        total: 108000,
        notes: 'Recurring costs for licenses and maintenance over 3 years'
      }
    ],
    capex_total: 0,
    opex_total: 0,
    total: 0,
    included: true
  };
}

/**
 * Create default training costs structure
 */
export function createDefaultTrainingCosts(): TrainingCosts {
  return {
    items: [
      {
        id: generateId(),
        item: 'Staff Training Program',
        quantity: 50,
        unit_cost: 2000,
        total: 100000,
        notes: '50 staff × $2K/person for FAIR training and certification'
      }
    ],
    total: 0,
    included: true
  };
}

/**
 * Create default migration costs structure
 */
export function createDefaultMigrationCosts(): MigrationCosts {
  return {
    items: [
      {
        id: generateId(),
        item: 'Legacy system assessment',
        quantity: 1,
        unit_cost: 50000,
        total: 50000,
        notes: 'Inventory and dependency mapping'
      },
      {
        id: generateId(),
        item: 'Data quality audit',
        quantity: 1,
        unit_cost: 75000,
        total: 75000,
        notes: 'Baseline quality metrics and gap analysis'
      },
      {
        id: generateId(),
        item: 'Historical data cleaning',
        quantity: 10000,
        unit_cost: 25,
        total: 250000,
        notes: '10,000 datasets @ $25/dataset'
      }
    ],
    total: 0,
    included: false  // Disabled by default - advanced category
  };
}

/**
 * Create default compliance costs structure (Pharma-specific)
 */
export function createDefaultComplianceCosts(): ComplianceCosts {
  return {
    items: [
      {
        id: generateId(),
        item: 'FDA/EMA audit readiness',
        cost: 60000,
        phase: 'Phase 1',
        notes: 'Inspection preparation and mock audits'
      },
      {
        id: generateId(),
        item: '21 CFR Part 11 compliance',
        cost: 100000,
        phase: 'Phase 1-2',
        notes: 'Electronic records and signatures'
      },
      {
        id: generateId(),
        item: 'ALCOA+ data integrity',
        cost: 75000,
        phase: 'Phase 2',
        notes: 'Attributable, Legible, Contemporaneous, Original, Accurate'
      }
    ],
    total: 0,
    included: false  // Disabled by default - pharma-specific
  };
}

/**
 * Create default vendor costs structure (includes FAIR Compass)
 */
export function createDefaultVendorCosts(): VendorCosts {
  return {
    items: [
      {
        id: generateId(),
        vendor_type: 'DATA Compass Platform',
        cost: 36000,
        duration: '12 months',
        total: 36000,
        notes: 'Annual license for FAIR assessment platform'
      },
      {
        id: generateId(),
        vendor_type: 'Implementation Consulting',
        cost: 9000,
        duration: '40 hours @ $225/hr',
        total: 9000,
        notes: 'Professional services for implementation support'
      }
    ],
    total: 0,
    included: true
  };
}

/**
 * Create default risk & contingency structure
 */
export function createDefaultRiskCosts(projectCostTotal: number = 500000): RiskCosts {
  const monthlyBurnRate = projectCostTotal / 36;

  return {
    reserves: [
      {
        reserve_type: 'Scope creep contingency',
        basis: 'Total project cost',
        percentage: 10,
        amount: Math.round(projectCostTotal * 0.10),
        user_override: false
      },
      {
        reserve_type: 'Schedule delay buffer',
        basis: 'Monthly burn rate × 2',
        percentage: 0,
        amount: Math.round(monthlyBurnRate * 2),
        user_override: false
      }
    ],
    total: 0,
    included: true
  };
}

/**
 * Create default operational disruption costs structure
 */
export function createDefaultDisruptionCosts(): DisruptionCosts {
  return {
    items: [
      {
        id: generateId(),
        item: 'Productivity dip during transition',
        quantity: 1,
        unit_cost: 50000,
        total: 50000,
        notes: 'Learning curve and workflow changes'
      }
    ],
    total: 0,
    included: false  // Disabled by default - advanced category
  };
}

/**
 * Create default financial parameters
 */
export function createDefaultFinancialParameters(): FinancialParameters {
  return {
    discount_rate: 10,    // 10% WACC (typical for pharma)
    tax_rate: 25,         // 25% corporate tax rate
    inflation_rate: 3,    // 3% annual inflation
    currency: 'USD'
  };
}

/**
 * Create default duration configuration
 */
export function createDefaultDurationConfig(): DurationConfig {
  const now = new Date();
  return {
    start_month: now.getMonth() + 1,
    start_year: now.getFullYear(),
    ongoing_ops_percent: 15,
    monthly_allocation: [
      3.0,   // Month 1: Planning (low)
      5.0,   // Month 2: Initial ramp-up
      7.0,   // Month 3: Building momentum
      9.0,   // Month 4: Increasing activity
      11.0,  // Month 5: Peak begins
      12.0,  // Month 6: Peak activity
      13.0,  // Month 7: Peak activity
      12.0,  // Month 8: Peak continues
      11.0,  // Month 9: Ramp-down begins
      8.0,   // Month 10: Declining activity
      5.0,   // Month 11: Winding down
      4.0    // Month 12: Closure (total = 100.0)
    ]
  };
}

/**
 * Create default project costs structure
 */
export function createDefaultProjectCosts(): ProjectCosts {
  const costs: ProjectCosts = {
    personnel: createDefaultPersonnelCosts(),
    technology: createDefaultTechnologyCosts(),
    training: createDefaultTrainingCosts(),
    migration: createDefaultMigrationCosts(),
    compliance: createDefaultComplianceCosts(),
    vendor: createDefaultVendorCosts(),
    risk: createDefaultRiskCosts(),
    disruption: createDefaultDisruptionCosts(),
    financial_params: createDefaultFinancialParameters(),
    duration: createDefaultDurationConfig(),
    total: 0,
    phase_breakdown: {
      'Phase 1 (Months 1-12)': 0,
      'Phase 2 (Months 13-24)': 0,
      'Phase 3 (Months 25-36)': 0
    }
  };

  // Calculate totals
  recalculateCosts(costs);

  return costs;
}

// ============================================================================
// Recalculation Logic
// ============================================================================

/**
 * Recalculate all costs and totals
 */
export function recalculateCosts(costs: ProjectCosts): void {
  // Personnel costs
  let personnelTotal = 0;
  let totalFTEHours = 0;
  let weightedRateSum = 0;

  costs.personnel.roles.forEach(role => {
    const totalHours = role.fte * role.duration_months * 160; // 160 hours/month
    const roleCost = totalHours * role.loaded_rate + role.opportunity_cost;
    personnelTotal += roleCost;
    totalFTEHours += totalHours;
    weightedRateSum += totalHours * role.loaded_rate;
  });

  costs.personnel.total = Math.round(personnelTotal);
  costs.personnel.blended_rate = totalFTEHours > 0
    ? Math.round(weightedRateSum / totalFTEHours)
    : 0;

  // Technology costs
  costs.technology.capex_total = costs.technology.capex.reduce((sum, item) => sum + item.cost, 0);
  costs.technology.opex.forEach(item => {
    item.total = item.monthly_cost * item.duration_months;
  });
  costs.technology.opex_total = costs.technology.opex.reduce((sum, item) => sum + item.total, 0);
  costs.technology.total = costs.technology.capex_total + costs.technology.opex_total;

  // Training costs
  costs.training.items.forEach(item => {
    item.total = item.quantity * item.unit_cost;
  });
  costs.training.total = costs.training.items.reduce((sum, item) => sum + item.total, 0);

  // Migration costs
  costs.migration.items.forEach(item => {
    item.total = item.quantity * item.unit_cost;
  });
  costs.migration.total = costs.migration.items.reduce((sum, item) => sum + item.total, 0);

  // Compliance costs
  costs.compliance.total = costs.compliance.items.reduce((sum, item) => sum + item.cost, 0);

  // Vendor costs
  costs.vendor.items.forEach(item => {
    item.total = item.cost;
  });
  costs.vendor.total = costs.vendor.items.reduce((sum, item) => sum + item.total, 0);

  // Disruption costs
  costs.disruption.items.forEach(item => {
    item.total = item.quantity * item.unit_cost;
  });
  costs.disruption.total = costs.disruption.items.reduce((sum, item) => sum + item.total, 0);

  // Calculate subtotal (before risk/contingency) - only include enabled categories
  const subtotal =
    (costs.personnel.included ? costs.personnel.total : 0) +
    (costs.technology.included ? costs.technology.total : 0) +
    (costs.training.included ? costs.training.total : 0) +
    (costs.migration.included ? costs.migration.total : 0) +
    (costs.compliance.included ? costs.compliance.total : 0) +
    (costs.vendor.included ? costs.vendor.total : 0) +
    (costs.disruption.included ? costs.disruption.total : 0);

  // Risk & contingency (update if not user-overridden)
  const monthlyBurnRate = subtotal / 36;
  costs.risk.reserves.forEach(reserve => {
    if (!reserve.user_override) {
      if (reserve.reserve_type.includes('Scope creep')) {
        reserve.amount = Math.round(subtotal * (reserve.percentage / 100));
      } else if (reserve.reserve_type.includes('Schedule delay')) {
        reserve.amount = Math.round(monthlyBurnRate * 2);
      }
    }
  });
  costs.risk.total = costs.risk.reserves.reduce((sum, reserve) => sum + reserve.amount, 0);

  // Total project costs
  costs.total = subtotal + (costs.risk.included ? costs.risk.total : 0);

  // Phase breakdown (simple 1/3 allocation)
  const phaseAllocation = costs.total / 3;
  costs.phase_breakdown = {
    'Phase 1 (Months 1-12)': Math.round(phaseAllocation),
    'Phase 2 (Months 13-24)': Math.round(phaseAllocation),
    'Phase 3 (Months 25-36)': Math.round(phaseAllocation)
  };
}

// ============================================================================
// Item Management
// ============================================================================

/**
 * Add a new item to a section
 */
export function addItem<T extends { id: string }>(items: T[], template: Omit<T, 'id'>): T {
  const newItem = {
    ...template,
    id: generateId()
  } as T;
  items.push(newItem);
  return newItem;
}

/**
 * Delete an item from a section
 */
export function deleteItem<T extends { id: string }>(items: T[], id: string): boolean {
  const index = items.findIndex(item => item.id === id);
  if (index !== -1) {
    items.splice(index, 1);
    return true;
  }
  return false;
}

/**
 * Duplicate an item in a section
 */
export function duplicateItem<T extends { id: string }>(items: T[], id: string): T | null {
  const index = items.findIndex(item => item.id === id);
  if (index !== -1) {
    const original = items[index];
    const duplicate = {
      ...original,
      id: generateId()
    };
    items.splice(index + 1, 0, duplicate);
    return duplicate;
  }
  return null;
}

// ============================================================================
// Summary Extraction
// ============================================================================

/**
 * Extract cost summary for Action Plan integration
 */
export function extractCostSummary(costs: ProjectCosts): CostSummary {
  return {
    total: costs.total,
    personnel: costs.personnel.included ? costs.personnel.total : 0,
    technology: costs.technology.included ? costs.technology.total : 0,
    training: costs.training.included ? costs.training.total : 0,
    migration: costs.migration.included ? costs.migration.total : 0,
    compliance: costs.compliance.included ? costs.compliance.total : 0,
    vendor: costs.vendor.included ? costs.vendor.total : 0,
    risk: costs.risk.included ? costs.risk.total : 0,
    disruption: costs.disruption.included ? costs.disruption.total : 0,
    currency: costs.financial_params.currency
  };
}

// ============================================================================
// Pre-population from Organization Context
// ============================================================================

export type OrgSize = 'small' | 'medium' | 'large';
export type Industry = 'pharma' | 'biotech' | 'diagnostics' | 'research' | 'healthcare';

interface OrgContext {
  size?: OrgSize;
  industry?: Industry;
  dataset_count?: number;
  headcount?: number;
}

/**
 * Adjust default costs based on organization context
 */
export function adjustCostsForOrganization(costs: ProjectCosts, context: OrgContext): void {
  const { size = 'medium', industry = 'pharma', headcount = 50 } = context;

  // Adjust labor rates by org size
  const laborMultiplier: Record<OrgSize, number> = {
    small: 0.7,
    medium: 1.0,
    large: 1.3
  };

  costs.personnel.roles.forEach(role => {
    role.loaded_rate = Math.round(role.loaded_rate * laborMultiplier[size]);
  });

  // Adjust training headcount
  costs.training.items.forEach(item => {
    if (item.item.toLowerCase().includes('training')) {
      item.quantity = headcount;
      item.total = item.quantity * item.unit_cost;
    }
  });

  // Adjust vendor costs (FAIR Compass pricing tiers)
  const vendorLicense: Record<OrgSize, number> = {
    small: 18000,
    medium: 36000,
    large: 75000
  };

  const consultingHours: Record<OrgSize, number> = {
    small: 20,
    medium: 40,
    large: 80
  };

  costs.vendor.items.forEach(item => {
    if (item.vendor_type.includes('DATA Compass')) {
      item.cost = vendorLicense[size];
      item.total = item.cost;
    } else if (item.vendor_type.includes('Consulting')) {
      const hours = consultingHours[size];
      const rate = size === 'large' ? 275 : size === 'small' ? 175 : 225;
      item.cost = hours * rate;
      item.total = item.cost;
      item.duration = `${hours} hours @ $${rate}/hr`;
    }
  });

  // Enable compliance for pharma
  if (industry === 'pharma') {
    costs.compliance.included = true;
  }

  // Recalculate totals
  recalculateCosts(costs);
}
